import "./AgreementRate.less";
import strings from "./AgreementRate.strings";
import {
    GuxBadge,
    GuxButton,
    GuxIcon,
    GuxIconTooltipBeta,
} from "genesys-spark-components-react";
import { useStrings } from "features/i18n/useStrings";
import { useAppSelector } from "app/hooks";
import {
    selectForm,
    selectIsAutomatedEvaluationForm,
} from "../qualityFormSlices";
import { selectLatestPublishedFormId } from "../qualityFormSlice";
import {
    getAutomatedEvaluationFormDetailRoutePath,
    getFormDetailRoutePath,
} from "util/routeUtils";
import FormType from "enums/FormType";
import embeddingService from "services/embeddingService";

export interface AgreementRateProps {
    agreementRate: number; // 0-1
    hasData: boolean;
}

type BadgeAccent = "success" | "warning" | "error";

const AGREEMENT_RATE_THRESHOLDS = {
    GOOD: 80,
    MODERATE: 60,
} as const;

const getBadgeAccent = (agreementRate: number): BadgeAccent => {
    const percentage = agreementRate * 100;

    if (percentage >= AGREEMENT_RATE_THRESHOLDS.GOOD) {
        return "success";
    } else if (percentage >= AGREEMENT_RATE_THRESHOLDS.MODERATE) {
        return "warning";
    } else {
        return "error";
    }
};

export function AgreementRate({ agreementRate, hasData }: AgreementRateProps) {
    const t = useStrings(strings);
    const isPublishedForm = useAppSelector((state) => {
        return selectForm(state)?.published ?? false;
    });
    const latestPublishedFormId = useAppSelector(selectLatestPublishedFormId);
    const isAutomatedEvaluationForm = useAppSelector(
        selectIsAutomatedEvaluationForm
    );

    // Only display if we have data - form validation is handled at Redux selector level
    if (!hasData) {
        return null;
    }

    const badgeAccent = getBadgeAccent(agreementRate);
    const percentage = Math.round(agreementRate * 100);
    const infoText = isPublishedForm
        ? t.tooltip.generalAgreementRateInfo
        : t.tooltip.draftFormAgreementRateInfo;

    const renderViewPublishedFormButton = () => {
        if (isPublishedForm || !latestPublishedFormId) return null;
        const formRoute = isAutomatedEvaluationForm
            ? getAutomatedEvaluationFormDetailRoutePath(
                  FormType.EVALUATION,
                  latestPublishedFormId
              )
            : getFormDetailRoutePath(
                  FormType.EVALUATION,
                  latestPublishedFormId
              );
        const openPublishedForm = () => {
            embeddingService.requestNavigation(formRoute, { newWindow: true });
        };
        return (
            <GuxButton
                accent="ghost"
                className="view-published-form-link"
                data-testid="view-published-form-button"
                onClick={openPublishedForm}
            >
                <span className="gux-body-sm-semibold">
                    {t.viewPublishedForm}
                </span>
                <GuxIcon
                    iconName="external-link"
                    screenreaderText={t.ariaLabels.externalLinkIcon}
                />
            </GuxButton>
        );
    };

    return (
        <div className="AgreementRate" data-testid="agreement-rate-tag">
            <span>
                {isPublishedForm ? t.label : t.draftFormAgreementRateLabel}
            </span>
            {isPublishedForm && (
                <GuxIconTooltipBeta icon-name="fa/circle-info-regular">
                    <span slot="content" data-testid="agreement-rate-tooltip">
                        {infoText}
                    </span>
                </GuxIconTooltipBeta>
            )}
            :
            <GuxBadge
                bold
                accent={badgeAccent}
                aria-label={`${t.ariaLabels.agreementRatePercentage({
                    percentage,
                })}. ${infoText}`}
                role="img"
            >
                {t.percentage({ percentage })}
            </GuxBadge>
            {renderViewPublishedFormButton()}
        </div>
    );
}
